'use client';

import { Box, keyframes, alpha } from '@mui/material';

// Keyframe animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.95);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const scaleIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

/**
 * Ultra-modern page loading component with animated delivery truck
 */
export function PageLoader({ text = 'Loading...', variant = 'default' }) {
  if (variant === 'dots') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          animation: `${scaleIn} 0.3s ease-out`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                animation: `${dotPulse} 1.4s ease-in-out infinite`,
                animationDelay: `${i * 0.16}s`,
              }}
            />
          ))}
        </Box>
        <Box
          component="span"
          sx={{
            mt: 3,
            fontSize: 14,
            fontWeight: 600,
            color: '#64748b',
            letterSpacing: '0.05em',
          }}
        >
          {text}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        animation: `${scaleIn} 0.3s ease-out`,
      }}
    >
      {/* Animated Delivery Truck SVG */}
      <Box
        sx={{
          position: 'relative',
          width: 120,
          height: 120,
          animation: `${float} 3s ease-in-out infinite`,
        }}
      >
        {/* Glowing background */}
        <Box
          sx={{
            position: 'absolute',
            inset: -20,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        />

        {/* Spinning loader ring */}
        <Box
          component="svg"
          viewBox="0 0 50 50"
          sx={{
            position: 'absolute',
            width: 120,
            height: 120,
            animation: `${spin} 2s linear infinite`,
          }}
        >
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="url(#loaderGradient)"
            strokeWidth="3"
            strokeLinecap="round"
            style={{
              animation: `${dash} 1.5s ease-in-out infinite`,
            }}
          />
          <defs>
            <linearGradient id="loaderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="50%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
        </Box>

        {/* Center truck icon */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 48,
            height: 48,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.35)',
          }}
        >
          <Box component="svg" viewBox="0 0 24 24" sx={{ width: 28, height: 28, fill: '#fff' }}>
            <path d="M18 18.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5m1.5-9H17V12h4.46L19.5 9.5M6 18.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5M20 8l3 4v5h-2c0 1.66-1.34 3-3 3s-3-1.34-3-3H9c0 1.66-1.34 3-3 3s-3-1.34-3-3H1V6c0-1.11.89-2 2-2h14v4h3M3 6v9h.76c.55-.61 1.35-1 2.24-1 .89 0 1.69.39 2.24 1H15V6H3z" />
          </Box>
        </Box>
      </Box>

      {/* Loading text with shimmer */}
      <Box
        sx={{
          mt: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: 16,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.02em',
          }}
        >
          {text}
        </Box>

        {/* Shimmer overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
            animation: `${shimmer} 1.5s infinite`,
          }}
        />
      </Box>

      {/* Road line animation */}
      <Box
        sx={{
          mt: 3,
          width: 120,
          height: 4,
          borderRadius: 2,
          bgcolor: '#e2e8f0',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            height: '100%',
            width: '40%',
            background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899)',
            borderRadius: 2,
            animation: `${shimmer} 1.2s ease-in-out infinite`,
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * Inline loader for smaller components
 */
export function InlineLoader({ size = 24, color = '#6366f1' }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 50 50"
      sx={{
        width: size,
        height: size,
        animation: `${spin} 1.5s linear infinite`,
      }}
    >
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        style={{
          animation: `${dash} 1.5s ease-in-out infinite`,
        }}
      />
    </Box>
  );
}

export default PageLoader;
